import GenericError from "@components/errors/GenericError";
import WebSlider from "@components/sliders/WebSlider";
import useIsMobile from "@utils/hooks/useIsMobile";
import React from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  isPathAllowedForPaysonic,
  isPathAllowedForPrimeBrazilOnly,
  isPaysonicHost,
  isPrimeBrazilOnlyHost,
} from "src/config/deploymentConfig";
import AffiliateManagement from "src/modules/affiliate-management/AffiliateManagement";
import Analytics from "src/modules/analytics/Analytics";
import AppConfig from "src/modules/app-config/AppConfig";
import Login from "src/modules/authentication/login";
import VerifyOtp from "src/modules/authentication/VerifyOtp";
import BalancesTabs from "src/modules/balances/BalancesTabs";
import BankManagement from "src/modules/bank-management/BankManagement";
import BillingTabs from "src/modules/billing/BillingTabs";
import CashierReferralProgram from "src/modules/cashier-referral/CashierReferralProgram";
import AutoTradeUpisManagement from "src/modules/cashier/AutoTradeUpisManagement";
import CashierManagement from "src/modules/cashier/CashierManagement";
import EmailLogs from "src/modules/email-logs/EmailLogs";
import ExchangeManagement from "src/modules/exchange-management/ExchangeManagement";
import Fintrac from "src/modules/fintrac/Fintrac";
import GroupManagement from "src/modules/group-management/GroupManagement";
import InstituionalManagement from "src/modules/instituitional-management/InstituionalManagement";
import InstituitionManagementDetails from "src/modules/instituitional-management/InstituitionManagementDetails";
import InvoiceApprovals from "src/modules/InvoiceApprovals/InvoiceApprovals";
import IpBlocklist from "src/modules/ip-blocklist/IpBlocklist";
import IvyAffiliateManagement from "src/modules/ivy-affiliate/IvyAffiliateManagement";
import KycTabs from "src/modules/Kyc/KycTabs";
import KycV2Page from "src/modules/Kyc/KycV2Page";
import MobileAffiliateManagement from "src/modules/mobile/affiliate-management/MobileAffiliateManagement";
import MobileAnalytics from "src/modules/mobile/analytics/MobileAnalytics";
import MobileAppConfig from "src/modules/mobile/app-config/MobileAppConfig";
import MobileLogin from "src/modules/mobile/authentication/MobileLogin";
import MobileVerifyOtp from "src/modules/mobile/authentication/MobileVerifyOtp";
import MobileBalancesTabs from "src/modules/mobile/balances/MobileBalancesTabs";
import MobileBankManagement from "src/modules/mobile/bank-management/MobileBankManagement";
import MobileBillingTabs from "src/modules/mobile/billing/MobileBillingTabs";
import MobileCashierReferralProgram from "src/modules/mobile/cashier-referral/MobileCashierReferralProgram";
import MobileCashierManagement from "src/modules/mobile/cashier/MobileCashierManagement";
import MobileExchangeManagement from "src/modules/mobile/exchange-management/MobileExchangeManagement";
import MobileGroupManagement from "src/modules/mobile/group-management/MobileGroupManagement";
import MobileInstitutionalManagement from "src/modules/mobile/institutional-management/MobileInstitutionalManagement";
import MobileInstitutionalManagementDetails from "src/modules/mobile/institutional-management/MobileInstitutionalManagementDetails";
import MobileInvoiceApprovals from "src/modules/mobile/invoice-approvals/MobileInvoiceApprovals";
import MobileIpBlocklist from "src/modules/mobile/ip-blocklist/MobileIpBlocklist";
import MobileIvyAffiliateManagement from "src/modules/mobile/ivy-affiliate/MobileIvyAffiliateManagement";
import MobileKycTabs from "src/modules/mobile/kyc/MobileKycTabs";
import MobileKycV2Page from "src/modules/mobile/kyc/MobileKycV2Page";
import MobileOfframpChargeManagement from "src/modules/mobile/offramp-charge-management/MobileOfframpChargeManagement";
import MobileOwnersManagement from "src/modules/mobile/owners/MobileOwnersManagement";
import MobilePrimeDisputeList from "src/modules/mobile/prime/MobilePrimeDisputeList";
import MobilePrimeIndiaVpnSettings from "src/modules/mobile/prime/MobilePrimeIndiaVpnSettings";
import MobilePrimeOrderFlowTabs from "src/modules/mobile/prime/MobilePrimeOrderFlowTabs";
import MobileRates from "src/modules/mobile/prime/MobileRates";
import MobileRevenue from "src/modules/mobile/revenue/MobileRevenue";
import MobileSettleAccounts from "src/modules/mobile/settle-accounts/MobileSettleAccounts";
import MobileCPGOrderFlow from "src/modules/mobile/transactions/cpg/MobileCPGOrderFlow";
import MobileUnifiedTransactions from "src/modules/mobile/transactions/unified/MobileUnifiedTransactions";
import MobileUserManagement from "src/modules/mobile/user_management/MobileUserManagement";
import OfframpChargeManagement from "src/modules/offramp-charge-management/OfframpChargeManagement";
import OwnersManagement from "src/modules/owners/OwnersManagement";
import PrimeDisputeList from "src/modules/Prime/PrimeDisputes/PrimeDisputeList";
import PrimeIndiaVpnSettings from "src/modules/Prime/PrimeIndiaVpnSettings";
import PrimeOrderFlowTabs from "src/modules/Prime/PrimeOrderFlowTabs";
import Rates from "src/modules/Prime/Rates/Rates";
import Revenue from "src/modules/revenue/Revenue";
import SettleAccounts from "src/modules/settleaccounts/SettleAccounts";
import CPGOrderFlow from "src/modules/Transactions/cpg/CPGOrderFlow";
import UnifiedTransactions from "src/modules/Transactions/unified_transactions/UnifiedTransactions";
import UserManagement from "src/modules/user_management/UserManagement";
import TransactionDetails from "src/modules/user_management/wallet/TransactionDetails";
import UsersListFlow from "src/modules/Users/Users";
import WalletBlocklist from "src/modules/wallet-blocklist/WalletBlocklist";
import Layout from "./Layout";
import MobileLayout from "./MobileLayout";
import ProtectedRoute from "./ProtectedRoutes";

const Responsive = ({
  web,
  mobile,
}: {
  web: React.ReactNode;
  mobile: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  return <>{isMobile ? mobile : web}</>;
};

const ResponsiveLayout = () => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileLayout />
  ) : (
    <Layout />
  );
};

const DeploymentRouteGuard = () => {
  const location = useLocation();

  if (
    isPrimeBrazilOnlyHost() &&
    !isPathAllowedForPrimeBrazilOnly(location.pathname)
  ) {
    return <Navigate to="/prime-order-flow" replace />;
  }

  if (isPaysonicHost() && !isPathAllowedForPaysonic(location.pathname)) {
    return <Navigate to="/transactions" replace />;
  }

  return <Outlet />;
};

const AppRouter = () => {
  const navigate = useNavigate();
  const isPaysonic = isPaysonicHost();

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={<Responsive web={<Login />} mobile={<MobileLogin />} />}
      />
      <Route
        path="/verifyOtp"
        element={
          <Responsive web={<VerifyOtp />} mobile={<MobileVerifyOtp />} />
        }
      />
      <Route path="/error" element={<GenericError />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DeploymentRouteGuard />}>
          <Route element={<ResponsiveLayout />}>
            <Route
              path="/"
              element={
                isPaysonic ? (
                  <UsersListFlow />
                ) : (
                  <Responsive
                    web={<UserManagement />}
                    mobile={<MobileUserManagement />}
                  />
                )
              }
            >
              <Route
                path=":transactionID/:ledger"
                element={
                  <WebSlider
                    isOpen={true}
                    onClose={() => navigate("/")}
                    activeComponent={<TransactionDetails />}
                    heading="Transactions"
                  />
                }
              />
            </Route>

            <Route
              path="/transactions"
              element={
                <Responsive
                  web={<UnifiedTransactions scope="external" />}
                  mobile={<MobileUnifiedTransactions scope="external" />}
                />
              }
            />
            <Route
              path="/internal-transactions"
              element={
                <Responsive
                  web={<UnifiedTransactions scope="internal" />}
                  mobile={<MobileUnifiedTransactions scope="internal" />}
                />
              }
            />

            <Route
              path="/cpg-order-flow"
              element={
                <Responsive
                  web={<CPGOrderFlow />}
                  mobile={<MobileCPGOrderFlow />}
                />
              }
            />
            <Route
              path="/wallet-blocklist"
              element={
                <Responsive
                  web={<WalletBlocklist />}
                  mobile={<WalletBlocklist />}
                />
              }
            />
            <Route
              path="/kyc"
              element={
                <Responsive web={<KycTabs />} mobile={<MobileKycTabs />} />
              }
            />
            <Route
              path="/kyc-v2"
              element={
                <Responsive web={<KycV2Page />} mobile={<MobileKycV2Page />} />
              }
            />
            <Route
              path="/prime-order-flow"
              element={
                <Responsive
                  web={<PrimeOrderFlowTabs />}
                  mobile={<MobilePrimeOrderFlowTabs />}
                />
              }
            />
            <Route
              path="/prime-order-flow/vpn-settings"
              element={
                <Responsive
                  web={<PrimeIndiaVpnSettings />}
                  mobile={<MobilePrimeIndiaVpnSettings />}
                />
              }
            />
            <Route
              path="/prime-dispute-resolution"
              element={
                <Responsive
                  web={<PrimeDisputeList />}
                  mobile={<MobilePrimeDisputeList />}
                />
              }
            />
            <Route
              path="/invoice-approvals"
              element={
                <Responsive
                  web={<InvoiceApprovals />}
                  mobile={<MobileInvoiceApprovals />}
                />
              }
            />
            <Route
              path="/invoice-approvals/off-ramp-charge-management"
              element={
                <Responsive
                  web={<OfframpChargeManagement />}
                  mobile={<MobileOfframpChargeManagement />}
                />
              }
            />
            <Route
              path="/invoice-approvals/exchange-management"
              element={
                <Responsive
                  web={<ExchangeManagement />}
                  mobile={<MobileExchangeManagement />}
                />
              }
            />
            <Route
              path="/invoice-approvals/bank-management"
              element={
                <Responsive
                  web={<BankManagement />}
                  mobile={<MobileBankManagement />}
                />
              }
            />
            <Route
              path="/rates"
              element={<Responsive web={<Rates />} mobile={<MobileRates />} />}
            />
            <Route
              path="/analytics"
              element={
                <Responsive web={<Analytics />} mobile={<MobileAnalytics />} />
              }
            />
            <Route
              path="/revenue"
              element={
                <Responsive web={<Revenue />} mobile={<MobileRevenue />} />
              }
            />
            <Route
              path="/billing"
              element={
                <Responsive
                  web={<BillingTabs />}
                  mobile={<MobileBillingTabs />}
                />
              }
            />
            <Route
              path="/cashier-management"
              element={
                <Responsive
                  web={<CashierManagement />}
                  mobile={<MobileCashierManagement />}
                />
              }
            />
            <Route
              path="/cashier-management/auto-trade-upis"
              element={<AutoTradeUpisManagement />}
            />
            <Route
              path="/cashier-referral-program"
              element={
                <Responsive
                  web={<CashierReferralProgram />}
                  mobile={<MobileCashierReferralProgram />}
                />
              }
            />
            <Route
              path="/institution-management"
              element={
                <Responsive
                  web={<InstituionalManagement />}
                  mobile={<MobileInstitutionalManagement />}
                />
              }
            />
            <Route
              path="/institution-management/:id"
              element={
                <Responsive
                  web={<InstituitionManagementDetails />}
                  mobile={<MobileInstitutionalManagementDetails />}
                />
              }
            />
            <Route
              path="/affiliate-management"
              element={
                <Responsive
                  web={<AffiliateManagement />}
                  mobile={<MobileAffiliateManagement />}
                />
              }
            />
            <Route
              path="/ivy-affiliate"
              element={
                <Responsive
                  web={<IvyAffiliateManagement />}
                  mobile={<MobileIvyAffiliateManagement />}
                />
              }
            />
            <Route
              path="/settle-accounts"
              element={
                <Responsive
                  web={<SettleAccounts />}
                  mobile={<MobileSettleAccounts />}
                />
              }
            />
            <Route
              path="/balances"
              element={
                <Responsive
                  web={<BalancesTabs />}
                  mobile={<MobileBalancesTabs />}
                />
              }
            />
            <Route
              path="/app-config"
              element={
                <Responsive web={<AppConfig />} mobile={<MobileAppConfig />} />
              }
            />
            <Route
              path="/groups"
              element={
                <Responsive
                  web={<GroupManagement />}
                  mobile={<MobileGroupManagement />}
                />
              }
            />
            <Route
              path="/ip-blocklist"
              element={
                <Responsive
                  web={<IpBlocklist />}
                  mobile={<MobileIpBlocklist />}
                />
              }
            />
            <Route
              path="/email-logs"
              element={
                <Responsive web={<EmailLogs />} mobile={<EmailLogs />} />
              }
            />
            <Route
              path="/owners"
              element={
                <Responsive
                  web={<OwnersManagement />}
                  mobile={<MobileOwnersManagement />}
                />
              }
            />
            <Route
              path="/fintrac"
              element={<Responsive web={<Fintrac />} mobile={<Fintrac />} />}
            />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRouter;
